import type { MouseEvent } from 'react';
import { Box, IconButton, Link, Stack, Typography } from '@mui/material';
import {
  externalResourceColors,
  externalResourceIconNames,
  resourceIconMap,
} from '@/constants/resources/resources-icons';
import type { ExternalLink } from '@/data/idp/plans/interfaces';
import { ResourceTypes } from '@/models/resources';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

const getMainDomain = (link?: string): string | undefined => {
  if (!link) return '';
  try {
    const url = link.startsWith('http') ? link : `http://${link}`;
    return new URL(url).host;
  } catch {
    return link.split('/')[0];
  }
};

interface SelectedResourceCardProps {
  resource?: ExternalLink;
  onRemove?: () => void;
  removable?: boolean;
}

export const SelectedResourceCard = ({
  resource,
  onRemove,
  removable,
}: SelectedResourceCardProps) => {
  const handleRemoveClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemove?.();
  };

  const category = resource?.category as Exclude<
    ResourceTypes,
    ResourceTypes.Others
  >;
  const icon = externalResourceIconNames[category];
  const bgColor = externalResourceColors[category];
  const IconComponent = category ? resourceIconMap[icon] : null;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      sx={{
        ...(removable && {
          p: 2,
          border: '1px solid',
          borderColor: 'neutral.200',
          borderRadius: 1,
        }),
      }}
    >
      <Stack gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          {IconComponent && (
            <Stack
              bgcolor={bgColor}
              height={24}
              width={24}
              borderRadius={1}
              alignItems="center"
              justifyContent="center"
            >
              <IconComponent fontSize="small" />
            </Stack>
          )}
          <Typography variant="h4" flex={1}>
            {resource?.name}
          </Typography>
        </Box>
        {resource?.url && (
          <Stack direction="row" alignItems="center" gap={1}>
            <LinkIcon fontSize="small" />
            <Link
              color="primary.dark"
              href={resource.url}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              {getMainDomain(resource.url)}
            </Link>
          </Stack>
        )}
      </Stack>
      {removable && (
        <IconButton
          onClick={handleRemoveClick}
          sx={{ color: 'neutral.500', ml: 1 }}
          aria-label="Remover curso"
        >
          <DeleteIcon fontSize="small" sx={{ color: 'primary.dark' }} />
        </IconButton>
      )}
    </Stack>
  );
};
